import React, { useEffect, useRef, useState } from "react";
import { Col, Form } from "react-bootstrap";
import * as workflowService from "../../../services/workflowService";
import useSkills from "../../../hooks/useSkills";
import "./WorkflowTimeline.css";
import {
  serviceFromPriceSection,
  pickDefinition,
  assignmentsFromExisting,
  mirroredStepKey,
  splitSteps,
  skillFor,
} from "./workflowAssignments";

// The booking form's Workflow panel: who does each step of this service's workflow.
// Everything here is optional - a booking is valid with nobody assigned - and the
// panel renders nothing for a service that has no workflow, so such a booking form
// is exactly what it was.

const CHECK_DEBOUNCE_MS = 350;

// Resolves the workflow for the booking being made and, when editing, the one it
// already has. A failure is the same as "no workflow".
export function useBookingWorkflow({ priceSection, groupId }) {
  const service = serviceFromPriceSection(priceSection);
  const serviceKey = service ? `${service.serviceId}:${service.offeringIds[0]}` : "";
  const [state, setState] = useState({ key: null, definition: null, initial: {}, existing: null });

  useEffect(() => {
    const key = `${serviceKey}|${groupId || ""}`;
    if (!serviceKey && !groupId) {
      setState({ key, definition: null, initial: {}, existing: null });
      return undefined;
    }
    let cancelled = false;
    (async () => {
      let resolved = null;
      let existing = null;
      try {
        if (serviceKey) {
          const res = await workflowService.resolveWorkflow({ serviceId: service.serviceId, offeringIds: service.offeringIds });
          resolved = res?.success ? res.data : null;
        }
      } catch (e) {
        resolved = null;
      }
      try {
        if (groupId) {
          const res = await workflowService.getBookingWorkflow(groupId);
          existing = res?.success ? res.data : null;
        }
      } catch (e) {
        existing = null;
      }
      if (cancelled) return;
      const definition = pickDefinition(resolved, existing);
      setState({ key, definition, initial: assignmentsFromExisting(existing, definition), existing });
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceKey, groupId]);

  // `existing` is the booking's own workflow when it already has one (an edit), else null.
  return { definition: state.definition, initialAssignments: state.initial, stateKey: state.key, existing: state.existing };
}

const rowStyle = { padding: "8px 0", borderBottom: "1px solid #F1F3F9" };
const captionStyle = { fontSize: "12px", color: "#6B7280" };
const selectStyle = { height: "38px", fontSize: "14px", borderRadius: "6px", border: "1px solid #E3EBF8" };

function Badge({ children, tone }) {
  return (
    <span
      style={{
        fontSize: "11px",
        padding: "1px 8px",
        borderRadius: "10px",
        marginLeft: "8px",
        background: tone === "req" ? "#FDE7EF" : "#F3F4F6",
        color: tone === "req" ? "#C2185B" : "#6B7280",
      }}
    >
      {children}
    </span>
  );
}

function StepShell({ step, caption, children, variant, index, isLast }) {
  const content = (
    <div className="d-flex flex-wrap align-items-center" style={{ gap: "8px" }}>
      <div style={{ flex: "1 1 180px", minWidth: "160px" }}>
        <span style={{ fontWeight: 600, fontSize: "14px" }}>{step.name}</span>
        <Badge tone={step.is_mandatory ? "req" : "opt"}>{step.is_mandatory ? "Required" : "Optional"}</Badge>
        <div style={captionStyle}>{caption}</div>
      </div>
      <div style={{ flex: "2 1 240px", minWidth: "200px" }}>{children}</div>
    </div>
  );
  // Timeline: the steps in the order they happen, joined by a line, so it is clear who does
  // what and what comes after what.
  if (variant === "timeline") {
    return (
      <div className="wf-tl-item" data-testid={`wf-step-${step.step_key}`}>
        <div className="wf-tl-rail" aria-hidden="true">
          <span className="wf-tl-dot">{index + 1}</span>
          {!isLast && <span className="wf-tl-line" />}
        </div>
        <div className="wf-tl-body">{content}</div>
      </div>
    );
  }
  return (
    <div style={rowStyle} data-testid={`wf-step-${step.step_key}`}>
      {content}
    </div>
  );
}

// props
//   definition / initialAssignments   from useBookingWorkflow
//   assignments { step_key: userId }  value; onChange(next) edits it
//   resources, resourceIds, resourceLabel   the booking's resource, chosen in its own field
//   mainPeople [names]                who the crew calendar has chosen (the mirrored step)
//   date / time / durationMin / excludeGroupId   what "busy" is judged against
//   variant "timeline" draws the steps on a vertical timeline (default: the plain list)
//   mirroredHint   what the service provider step says before a crew member is chosen
function WorkflowPanel({
  definition,
  initialAssignments,
  assignments,
  onChange,
  resources,
  resourceIds,
  resourceLabel = "Resource",
  mainPeople = [],
  date,
  time,
  durationMin,
  excludeGroupId,
  disabled,
  variant,
  mirroredHint = "Chosen in the calendar above",
}) {
  const { labelFor } = useSkills();
  const [options, setOptions] = useState({});
  const [showEveryone, setShowEveryone] = useState(false);
  const [showOptional, setShowOptional] = useState(false);
  const touched = useRef(new Set());
  const latest = useRef(assignments);
  latest.current = assignments;

  const steps = definition?.steps || [];
  const stepsKey = steps.map((s) => `${s.step_key}:${s.skill_key || ""}:${s.kind}`).join("|");
  const mirrored = mirroredStepKey(steps);

  // People already on a saved booking count as chosen: the form must not replace them on its own.
  useEffect(() => {
    touched.current = new Set(Object.keys(initialAssignments || {}));
  }, [definition?.template_id, initialAssignments]);

  useEffect(() => {
    setShowEveryone(false);
  }, [resourceIds]);

  // Who can fill each skill's steps (one request per distinct skill), then fill what has
  // exactly one obvious answer: the single person mapped to the chosen resource.
  useEffect(() => {
    const crewSteps = steps.filter((s) => s.kind === "CREW" && s.step_key !== mirrored);
    if (!crewSteps.length) {
      setOptions({});
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      const skills = [...new Set(crewSteps.map(skillFor))];
      const entries = await Promise.all(
        skills.map(async (skill) => {
          try {
            const res = await workflowService.getEligibleCrew({
              skill,
              resourceId: resourceIds || undefined,
              date,
              time,
              durationMin,
              excludeGroupId,
            });
            return [skill, res?.success ? res.data : { crew: [], pool_size: 0, auto_pick: null }];
          } catch (e) {
            return [skill, { crew: [], pool_size: 0, auto_pick: null }];
          }
        })
      );
      if (cancelled) return;
      const next = Object.fromEntries(entries);
      setOptions(next);

      if (!resourceIds) return;
      const fill = {};
      crewSteps.forEach((s) => {
        const pick = next[skillFor(s)]?.auto_pick;
        if (pick && !touched.current.has(s.step_key) && !latest.current?.[s.step_key]) fill[s.step_key] = pick;
      });
      if (Object.keys(fill).length) onChange({ ...latest.current, ...fill });
    }, CHECK_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepsKey, resourceIds, date, time, durationMin, excludeGroupId]);

  if (!definition || !steps.length) return null;

  const choose = (stepKey, value) => {
    touched.current.add(stepKey);
    onChange({ ...assignments, [stepKey]: value ? Number(value) : null });
  };

  const chosenResource = (resources || []).find((r) => String(r.id) === String(resourceIds));

  const renderStep = (step, i) => {
    const mode = i === 0 ? "" : step.mode === "PARALLEL" ? "at the same time as the step before" : "after the step before";

    if (step.kind === "RESOURCE") {
      return (
        <StepShell key={step.step_key} step={step} variant={variant} index={i} isLast={i === steps.length - 1} caption={[resourceLabel, mode].filter(Boolean).join(" · ")}>
          <div style={{ fontSize: "14px" }}>
            {chosenResource ? (
              <strong>{chosenResource.name}</strong>
            ) : (
              <span style={captionStyle}>Not chosen yet. Use the {resourceLabel} field above.</span>
            )}
          </div>
        </StepShell>
      );
    }

    const caption = [step.skill_key ? labelFor(step.skill_key) || step.skill_key : "Anyone", mode].filter(Boolean).join(" · ");

    if (step.step_key === mirrored) {
      return (
        <StepShell key={step.step_key} step={step} variant={variant} index={i} isLast={i === steps.length - 1} caption={caption}>
          <div style={{ fontSize: "14px" }}>
            {mainPeople.length ? (
              <strong>{mainPeople.join(", ")}</strong>
            ) : (
              <span style={captionStyle}>{mirroredHint}</span>
            )}
          </div>
        </StepShell>
      );
    }

    const data = options[skillFor(step)] || { crew: [], pool_size: 0 };
    const current = assignments?.[step.step_key] ? Number(assignments[step.step_key]) : null;
    const limited = !!resourceIds && data.pool_size > 0 && !showEveryone;
    const list = data.crew.filter((c) => !limited || c.in_pool || c.user_id === current);
    const known = list.some((c) => c.user_id === current);

    return (
      <StepShell key={step.step_key} step={step} variant={variant} index={i} isLast={i === steps.length - 1} caption={caption}>
        <Form.Select
          size="sm"
          style={selectStyle}
          aria-label={step.name}
          value={current || ""}
          disabled={disabled}
          onChange={(e) => choose(step.step_key, e.target.value)}
        >
          <option value="">Not assigned</option>
          {current && !known && <option value={current}>{`Person #${current}`}</option>}
          {list.map((c) => (
            <option key={c.user_id} value={c.user_id}>
              {c.name}
              {resourceIds && c.in_pool ? " ★" : ""}
              {c.busy ? ` (busy: ${c.busy_with || "another booking"})` : ""}
            </option>
          ))}
        </Form.Select>
        {limited && (
          <button
            type="button"
            className="btn btn-link p-0"
            style={{ fontSize: "12px" }}
            onClick={() => setShowEveryone(true)}
          >
            Show everyone
          </button>
        )}
      </StepShell>
    );
  };

  const split = splitSteps(definition);
  const indexOf = (step) => steps.findIndex((s) => s.step_key === step.step_key);
  // The service step is always shown, even when it is Optional: it is the booking's main step (the
  // person chosen in the calendar, or "assign later"), not an extra to tuck away.
  const required = [...split.required, ...split.optional.filter((s) => s.step_key === mirrored)].sort((a, b) => indexOf(a) - indexOf(b));
  const optional = split.optional.filter((s) => s.step_key !== mirrored);
  // Optional steps are tucked away unless someone is already on one.
  const optionalOpen = showOptional || optional.some((s) => assignments?.[s.step_key]);

  return (
    <Col xs={12} style={{ marginBottom: "1%" }}>
      <div className="rounded border p-3" style={{ borderColor: "#E3EBF8" }} data-testid="workflow-panel">
        <div style={{ fontWeight: 600, fontSize: "15px" }}>{definition.name}</div>
        <div style={captionStyle}>Everything here is optional. You can assign people later from the booking.</div>
        {required.map((s) => renderStep(s, indexOf(s)))}
        {optional.length > 0 && (
          <>
            <button
              type="button"
              className="btn btn-link p-0 mt-2"
              style={{ fontSize: "13px" }}
              onClick={() => setShowOptional(!optionalOpen)}
            >
              {optionalOpen ? "Hide optional steps" : `Optional steps (${optional.length})`}
            </button>
            {optionalOpen && optional.map((s) => renderStep(s, indexOf(s)))}
          </>
        )}
      </div>
    </Col>
  );
}

export default WorkflowPanel;
